import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, User, Tag, ArrowLeft, Loader2, AlertCircle, BookOpen, ChevronRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { fetchBlogById, fetchBlogs } from '../services/api';

const CAT_STYLE = {
  'Exam Tips':      'bg-blue-500/10 text-blue-400 border-blue-500/20',
  'Study Methods':  'bg-green-500/10 text-green-400 border-green-500/20',
  'Motivation':     'bg-purple-500/10 text-purple-400 border-purple-500/20',
  'Subject Guides': 'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const CATEGORIES = ['Exam Tips', 'Study Methods', 'Motivation', 'Subject Guides'];

const SingleBlog = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [post,          setPost]         = useState(null);
  const [recentPosts,   setRecentPosts]  = useState([]);
  const [loading,       setLoading]      = useState(true);
  const [error,         setError]        = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([fetchBlogById(id), fetchBlogs()])
      .then(([postRes, allRes]) => {
        if (postRes.status === 'fulfilled') setPost(postRes.value.data);
        else setError('Blog post not found.');
        if (allRes.status === 'fulfilled') {
          setRecentPosts((allRes.value.data || []).filter(b => b._id !== id).slice(0, 5));
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh]">
        <Loader2 size={32} className="animate-spin text-accentBlue mb-4" />
        <p className="text-gray-500 text-sm">Loading article…</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <AlertCircle size={48} className="text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-white mb-2">Post Not Found</h2>
        <p className="text-gray-400 text-sm mb-6">{error}</p>
        <button onClick={() => navigate('/blog')}
          className="px-5 py-2.5 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue/15 transition-colors">
          Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">

      {/* Back */}
      <button onClick={() => navigate('/blog')}
        className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-8 group transition-colors">
        <ArrowLeft size={15} className="group-hover:-translate-x-1 transition-transform" /> Back to Blog
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-10">

        {/* ── Article ── */}
        <motion.article initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="lg:col-span-3">

          {/* Thumbnail */}
          {post.thumbnail ? (
            <img src={post.thumbnail} alt={post.title}
              className="w-full h-72 object-cover rounded-2xl border border-white/10 mb-8" />
          ) : (
            <div className="w-full h-56 bg-white/5 border border-white/10 rounded-2xl flex items-center justify-center mb-8">
              <BookOpen size={48} className="text-gray-700" />
            </div>
          )}

          {/* Meta */}
          <div className="flex flex-wrap items-center gap-3 mb-5">
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full border ${CAT_STYLE[post.category] || 'bg-white/10 text-gray-300 border-white/20'}`}>
              {post.category}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <Calendar size={12} /> {fmtDate(post.postedDate || post.createdAt)}
            </span>
            <span className="flex items-center gap-1.5 text-xs text-gray-400">
              <User size={12} /> {post.author || 'Admin'}
            </span>
          </div>

          {/* Title */}
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white leading-tight mb-6">{post.title}</h1>

          {/* Description */}
          {post.description && (
            <p className="text-gray-300 text-base leading-relaxed mb-6 font-medium border-l-4 border-accentBlue/40 pl-4 italic">
              {post.description}
            </p>
          )}

          {/* Content */}
          <div className="text-gray-300 text-sm leading-8 whitespace-pre-line mb-8">{post.content}</div>

          {/* Tags */}
          {(post.tags || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2 pt-6 border-t border-white/8">
              <Tag size={14} className="text-accentBlue" />
              {post.tags.map(t => (
                <span key={t} className="text-xs px-3 py-1 bg-white/8 border border-white/10 text-gray-300 rounded-full">{t}</span>
              ))}
            </div>
          )}
        </motion.article>

        {/* ── Sidebar ── */}
        <aside className="space-y-6">

          {/* Recent posts */}
          <div className="bg-white/4 border border-white/10 rounded-2xl p-5">
            <h3 className="font-bold text-white text-sm mb-4">Recent Posts</h3>
            {recentPosts.length === 0 ? (
              <p className="text-gray-500 text-xs">No other posts yet.</p>
            ) : (
              <div className="space-y-3">
                {recentPosts.map(b => (
                  <Link key={b._id} to={`/blog/${b._id}`}
                    className="flex items-start gap-2 text-xs text-gray-300 hover:text-accentBlue transition-colors group">
                    <ChevronRight size={13} className="shrink-0 mt-0.5 text-gray-600 group-hover:text-accentBlue transition-colors" />
                    <span className="line-clamp-2">{b.title}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {/* Categories */}
          <div className="bg-white/4 border border-white/10 rounded-2xl p-5">
            <h3 className="font-bold text-white text-sm mb-4">Categories</h3>
            <div className="space-y-2">
              {CATEGORIES.map(cat => (
                <Link key={cat} to="/blog"
                  className={`block text-xs px-3 py-2 rounded-xl border transition-all ${CAT_STYLE[cat] || 'bg-white/5 text-gray-400 border-white/10'} hover:opacity-80`}>
                  {cat}
                </Link>
              ))}
            </div>
          </div>

        </aside>
      </div>
    </div>
  );
};

export default SingleBlog;
